import "@uppy/core/css/style.min.css";
import "@uppy/dashboard/css/style.min.css";

import { Nav, NavItem, NavLink } from "reactstrap";
import { useContext, useEffect, useRef, useState } from "react";

import Dashboard from "@uppy/react/dashboard";
import { FormContext } from "./FormTemplate";
import Tus from "@uppy/tus";
import Uppy from "@uppy/core";
import _ from "lodash";
import classnames from "classnames";
import { jsonContentType } from "../../services/backendRequests";
import tusUrlStorage from "../../services/tusUrlStorage";
import { useInterval } from "react-use";
import { useSelector } from "react-redux";

const tabStyle = { cursor: "pointer" };
export let uploadInstances = new Map();

// Identities of every file accepted in the session, all datasets together: each
// tab is a separate Uppy instance, so the same file dropped in two tabs would be
// uploaded twice. Kept as a Set and updated as files come and go, because reading
// the instances back on every added file is quadratic and a 12k-file folder makes
// that unusable.
let sessionIdentities = new Set();

export const resetUploadInstances = () => {
  uploadInstances = new Map();
  sessionIdentities = new Set();
};

// Name, size and modification date: Uppy's own file id also carries the folder
// path, so the same file picked on its own and then inside a folder looks like
// two different files. These three values, on the other hand, are never shared by
// two same-named shots coming from two different flights (CLOV-581).
const fileIdentity = (file) =>
  `${file.name}-${file.data.size}-${file.data.lastModified}`;

const DatasetsTab = ({ number, isActive, toggle }) => {
  const context = useContext(FormContext);
  const uppy = uploadInstances.get(number);
  const [progress, setProgress] = useState();
  useInterval(() => {
    setProgress(uppy?.getState().totalProgress);
  }, 500);

  return (
    <NavItem>
      <NavLink
        style={tabStyle}
        className={classnames({
          noselect: true,
          active: isActive,
          collapseToggleTitle: !isActive,
        })}
        onClick={() => toggle(number)}
      >
        <i
          className={classnames({
            "me-1": true,
            "fa-regular fa-folder-open": isActive,
            "fa-solid fa-folder": !isActive,
          })}
          aria-hidden="true"
        />
        Dataset {context.datasetCount + number + 1}{" "}
        {progress ? <span>({progress}%)</span> : null}
      </NavLink>
    </NavItem>
  );
};

const DatasetsUpload = ({ id, options }) => {
  const context = useContext(FormContext);
  const statusFinish = useRef(context.statusFinish);
  statusFinish.current = context.statusFinish;

  const sessionUploaderId = `${context.session.id}/${context.datasetCount}/${id}`;
  const user = useSelector((state) => state.user);

  const datasetId = context.datasetCount + id + 1;

  if (!uploadInstances.has(sessionUploaderId)) {
    // One message per batch, not per file: uppy.info() stacks a message with its
    // own timer, so 15 duplicates meant 15 pills appearing and vanishing at the
    // same instant, and a 12k-file folder would schedule 12k timers. The flush
    // runs once the whole batch has gone through onBeforeFileAdded.
    let skipped = 0;
    let flushSkipped = null;
    const reportSkipped = () => {
      skipped += 1;
      clearTimeout(flushSkipped);
      flushSkipped = setTimeout(() => {
        uppy.info(
          `${skipped} file${skipped > 1 ? "s" : ""} already in this upload ` +
            `session, skipped`,
          "warning",
          4000
        );
        skipped = 0;
      }, 0);
    };

    const uppy = new Uppy({
      ...(options ?? {}),
      autoProceed: false,
      allowMultipleUploads: false,
      onBeforeUpload: () => true,
      onBeforeFileAdded: (currentFile) => {
        const identity = fileIdentity(currentFile);
        if (sessionIdentities.has(identity)) {
          reportSkipped();
          return false;
        }
        // Registered here rather than on the file-added event, so a file and its
        // folder dropped in one go are compared against each other: the whole
        // batch goes through this callback before any event is emitted.
        sessionIdentities.add(identity);

        const modifiedFile = {
          ...currentFile,
          // Uppy builds the id from the folder path, and @uppy/tus derives the tus
          // fingerprint from that id, so the resume key inherited two flaws: the
          // same file picked directly and inside a folder got two keys, and the
          // same file sent in another session reused this session's key, resuming
          // its upload and landing in the wrong session.
          //
          // Keyed on the session, and deliberately not on the dataset: a file
          // already sent in this session must stay attached to the dataset that
          // received it first, so a later send is recognised as done and nothing
          // is uploaded twice.
          id: [
            context.session.id,
            currentFile.name,
            currentFile.data.size,
            currentFile.data.lastModified,
          ].join("-"),
          meta: {
            ...currentFile.meta,
            relativePath: context.session.id + "___" + currentFile.name,
          },
        };
        return modifiedFile;
      },
      meta: {
        session_id: context.session.id,
        category: `dataset_${datasetId}`,
        user_id: user.self.id,
      },
    });

    uppy.use(Tus, {
      endpoint: process.env.REACT_APP_TUS_URL,
      chunkSize: 5 * 1024 * 1024,
      headers: user.identity,
      limit: 10,
      // Keep resume state out of localStorage: a 10k-file session blows its quota, and the
      // resulting error is reported as "tus: failed to create upload" that no retry can fix.
      urlStorage: tusUrlStorage,
    });

    // A file registered in onBeforeFileAdded but dropped afterwards, by a
    // restriction or by the user, must stop blocking its own re-adding.
    const forget = (file) =>
      file && sessionIdentities.delete(fileIdentity(file));
    uppy.on("file-removed", forget);
    uppy.on("restriction-failed", forget);

    uppy.on("complete", () => {
      // Read the files, not totalProgress: that one is written through a 500ms
      // throttle and can still hold its previous value when this event fires,
      // which would skip the session status update. An empty dataset counts as
      // done, every() being true on an empty list.
      const allCompleted = Array.from(uploadInstances.values()).every((v) =>
        Object.values(v.getState().files).every(
          (file) => file.progress?.uploadComplete
        )
      );
      if (allCompleted)
        fetch(`/api/client/upload_sessions/${context.session.id}`, {
          method: "PUT",
          body: JSON.stringify({ status: statusFinish.current }),
          headers: jsonContentType(user.identity),
        });
    });

    uppy.on("back-online", () => {
      uppy.retryAll();
    });

    uploadInstances.set(sessionUploaderId, uppy);
  }

  const uppyInstance = uploadInstances.get(sessionUploaderId);
  return (
    <Dashboard
      uppy={uppyInstance}
      hideUploadButton={true}
      proudlyDisplayPoweredByUppy={false}
      width="100%"
      height="37vh"
      showLinkToFileUploadResult={false}
      // Offers "browse folders" next to "browse files": the default only lets the
      // picker select files one by one, while a flight is a whole directory. The
      // two links cannot be merged, a browser picker is either in file mode or in
      // folder mode, so the wording is shortened to hold one line instead.
      fileManagerSelectionType="both"
      locale={{
        strings: {
          dropPasteBoth:
            "Drop files or folders here, or browse %{browseFiles} / %{browseFolders}",
          browseFiles: "files",
          browseFolders: "folders",
        },
      }}
    />
  );
};

const datasetsCount = new Map();
export default function DatasetUploader({ options }) {
  const context = useContext(FormContext);
  let datasetCount = datasetsCount.get(context.session.id) ?? 1;
  const [datasets, setDatasets] = useState(datasetCount);
  const [datasetArray, setDatasetsArray] = useState([0]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => setDatasetsArray([..._.range(datasets)]), [datasets]);
  useEffect(() => {
    setDatasets(1);
    setActiveTab(0);
  }, [context.datasetCount]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const disabled = context.loading || !context.editable;
  return (
    <div className={context.editable ? undefined : "upload-uploader-locked"}>
      <Nav tabs>
        {datasetArray.map((num) => (
          <DatasetsTab
            isActive={activeTab === num}
            number={num}
            toggle={toggle}
            key={num}
          />
        ))}

        <NavItem>
          <NavLink
            disabled={disabled}
            style={tabStyle}
            onClick={() => {
              setDatasets(datasets + 1);
              datasetsCount.set(context.session.id, datasets);
              setActiveTab(datasets);
            }}
            data-tip="Add a new dataset"
            data-type="success"
            data-place="top"
          >
            <i
              className={`fa-solid fa-lg fa-plus text-${
                disabled ? "secondary" : "success"
              }`}
            />
          </NavLink>
        </NavItem>

        <NavItem>
          {datasets !== 1 && (
            <NavLink
              disabled={disabled}
              style={tabStyle}
              onClick={() => {
                const activeTabIsLastTab = datasets - 1 === activeTab;
                if (activeTabIsLastTab) setActiveTab(activeTab - 1);
                setDatasets(datasets - 1);
              }}
              data-tip="Remove last dataset"
              data-type="error"
              data-place="top"
            >
              <i
                className={`fa-solid fa-lg fa-minus text-${
                  disabled ? "secondary" : "danger"
                }`}
              />
            </NavLink>
          )}
        </NavItem>
      </Nav>

      <DatasetsUpload id={activeTab} options={options} />
    </div>
  );
}
